import fetchIntercept from 'fetch-intercept';
import i18next from "i18next";
import i18nextHttpBackend from "i18next-http-backend";
import { getUser } from "./auth/api";
import { showMaterialSelection } from "./components/materials/materialSelection";
import { refreshProductSelector, resetProductSelector } from "./components/products/productSelection";
import type { SummaryAccessory, SummaryRow } from "./components/summary/summary";
import { askPlacement } from "./components/viewport/placement";
import { hideEntityActions, showEntityActions } from "./configurator/actions";
import { hideAvailableConnectors, updateAvailableConnectors } from "./configurator/connectors";
import { constructEntities, constructModule, getProductVariant } from "./configurator/construction";
import { dragCanvas, dragEndCanvas, dragStartCanvas, dragUI, dragUIStart, handleDragEnd, hideGhostEntity } from "./configurator/dragdrop";
import { deleteEntity, getCurrentEntity, getParentMesh, instantiateEntity, instantiateModule } from "./configurator/entities";
import { EntityHelper } from "./configurator/entity-helper";
import { constructLegs, createLegAttachments } from "./configurator/legs";
import { ModuleHelper } from "./configurator/module-helper";
import { onProgress } from "./configurator/progress";
import { AppContext, createAppContext } from "./core/context";
import { registerLegacyApp } from "./core/legacy-bridge";
import { materialLibrary, meshLibrary, productLibrary, projectLibrary, seriesLibrary } from "./data";
import { createEngine, createEnvironment, createGround, createLight, createMainCamera, createRenderTargetCamera, createScene, createShadowgenerator } from "./engine";
import { dragDropHandler, PDFHandler } from "./handlers";
import { adjustCameraView, resetMainCamera } from "./scene/camera";
import { hideAllHighlights, hideHighlight, showHighlight } from "./scene/highlight";
import { getTargetPosition, getZeroPlaneVector } from "./scene/pointer";
import { cleanupAssets, enableDebug, pauseRender, resetGUI, resumeRender } from "./scene/renderer";
import { renderConfigurationImage, renderConfigurationImages, renderConfigurationThumbnail, renderDrawingWithGUI } from "./scene/screenshot";
import { addError, appStore, resetErrors } from "./stores/app";
import "./styles/site.less";
import { Configuration, ConfigurationData, Entity, Material, Module, Project } from "./types/type_def";
import { FormatHelper } from "./utils/format";
import { ProductHelper } from "./utils/product";

/**
 * Starts the configurator (called from main.ts once the Vue app is mounted).
 */
export function startApp() {

    const ctx: AppContext = createAppContext();
    ctx.easingFunction = new BABYLON.BackEase(.0);
    ctx.easingFunction.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEOUT);

    ctx.moduleHelper = new ModuleHelper();
    ctx.entityHelper = new EntityHelper()

    ctx.moduleHelper.scaleFactor = ctx.scaleFactor;
    ctx.moduleHelper.entityHelper = ctx.entityHelper;

    //Handlers 
    let pdfHandler = new PDFHandler();

    // UI actions used by the configurator code (implemented below)
    ctx.ui = {
        showLoadingCanvas: () => showLoadingCanvas(),
        hideLoadingCanvas: () => hideLoadingCanvas(),
        createEntityPlacementPopup: (entity, matchingConnectors) => createEntityPlacementPopup(entity, matchingConnectors),
        handleModuleUpdating: (callback) => handleModuleUpdating(callback)
    };

    // Flows in this file that the Vue components trigger (implemented below)
    registerLegacyApp({
        onLoggedIn: (user) => {
            ctx.user = user;
            handleLogin();
        },
        onLoginFailed: () => handleLogin(),
        onBackClicked: () => handleBackClicked(),
        onSummaryClicked: () => handleSummaryClicked(),
        onProjectsClicked: () => handleProjectsClicked(),
        openConfiguration: async (identifier) => { await openConfig(identifier); },
        startNewProject: (project) => {
            ctx.currentProject = project;
            ctx.isNewConfig = true;
            showPage("series");
        },
        selectSeries: (seriesId) => selectSeries(seriesId),
        startConfiguring: () => startConfiguring(),
        onMaterialSelected: () => handleMaterialSelected(),
        onProductClicked: (productNumber, event) => handleProductClicked(productNumber, event),
        loadOptions: async () => ({
            series: await seriesLibrary.fetchSeriesByName(ctx.currentConfiguration.series),
            summaryElements: (await ctx.moduleHelper.generateModuleSummaryList(ctx.currentModule, ctx.currentConfiguration)).elements,
            configuration: ctx.currentConfiguration,
            module: ctx.currentModule,
        }),
        onOptionsChanged: async () => {
            await updateSummaryMenuTotalPrice();
            await saveProjectConfiguration();
        },
        renderProductImages: () => new Promise(resolve => renderConfigurationImages(ctx, ctx.currentConfiguration, () => resolve())),
        selectConfiguration: (id) => selectConfiguration(id),
        deleteConfiguration: (id) => deleteConfiguration(id),
        newConfiguration: () => newConfiguration(),
        onClickOutsideViewport: () => handleClickOutsideViewport(),
        onAccessoryAmountChanged: (productNumber, amount) => handleAccessoryAmountChanged(productNumber, amount),
        placeOrder: () => placeOrder(),
    });

    //Check iOS version and show alert if not high enough
    const isAtLeastiOS17 = CSS.supports("contain-intrinsic-size", "100px");   
    if (!isAtLeastiOS17) {
        alert("A newer iOS than 17 is needed in order to run the configurator.");
    }

    i18next.use(i18nextHttpBackend).init({
        lng: 'en', // if you're using a language detector, do not define the lng option
        fallbackLng: 'en',
        ns: ['ui'],
        debug: true,
        backend: {
            loadPath: 'static/locales/{{lng}}/{{ns}}.json'
        }
    }).then(async () => {

        ctx.resetPassword = window.location.href.toLowerCase().includes("function=resetpassword")
    
        if (window.location.href.toLowerCase().includes("seriesid")) { 

            appStore.loading = true;

            ctx.isSeriesConstrained = true;

            var params = new URLSearchParams(window.location.search.toLowerCase());
            var seriesId = params.get("seriesid");
            ctx.seriesConstrainedId = parseInt(seriesId);

            // B2C: logs in anonymously for the series in the URL
            const onB2CLoggedIn = async function (authJSON) {
                if (authJSON.success) {
                
                    ctx.user = await getUser();
                    ctx.isB2C = ctx.user.isB2C;

                    // B2C: no user menu and no prices in the header
                    appStore.header.isB2C = true;

                    const series = await seriesLibrary.fetchSeriesByID(ctx.seriesConstrainedId)
                    if (series) {

                        materialLibrary.defaultMaterial = series.defaultMaterial;
                        materialLibrary.materials = series.materials;        

                        let project = {} as Project;
                        project.seller = "B2C";
                        project.name = "B2C";
                        project.phone = "";
                        project.status = 1;
                        project.configurations = [{
                            series: series.name,
                            data: {
                                id: "configuration" + Date.now(),
                                offsetY: 0,
                                material: "",
                                thumbnail: "",
                                leg: "",
                                modules: [],
                                options: {},
                                customOptions: []
                            } as ConfigurationData                                                     
                        } as Configuration];

                        const configuration = project.configurations[0];
                        var defaultLeg = seriesLibrary.getSeriesLeg(series, "standard");
                        configuration.data.options["leg"] = defaultLeg.productNumber;
                        
                        var defaultPackageLeg = seriesLibrary.getSeriesLegPackage(series, "standard");              
                        configuration.data.leg = defaultPackageLeg.identifier;

                        var newproject = await projectLibrary.createProject(project);
                        if (newproject) {

                            ctx.currentProject = newproject;
                            ctx.currentProject.name = `B2C (${newproject.identifier})`;
                            ctx.currentConfiguration = newproject.configurations[0];                                                              
                           
                            await projectLibrary.saveProject(ctx.currentProject);

                            renderProject().then(() => {
                                instantiateModule(ctx, ctx.currentConfiguration, ctx.currentModule, async function (module, moduleNode) {
                                    ctx.currentModule = module;
                                    ctx.mainCamera.radius = ctx.initialCameraRadius;
                                    appStore.loading = false;
                                    await updateSummaryMenuTotalPrice();
                                });
                            });
                        }
                    }

                }
            };
            const onB2CFailed = function () {
                appStore.loading = false;
                handleLogin();
            };
            fetch("api/auth/b2c", { method: "POST", headers: { "Content-Type": "application/json" } })
                .then(async response => response.ok ? onB2CLoggedIn(await response.json()) : onB2CFailed(), onB2CFailed);
                       
        } else {       

            ctx.user = await getUser();
            if (!ctx.user || ctx.user.isB2C) {
                await fetch("api/auth/logout", { method: "POST", credentials: 'include' });
            }

            appStore.header.logoLink = true;
            appStore.loading = false;
            handleLogin();

        }
    });

    window.addEventListener("thumbnail_rendered", function (e: Event) {
        updateConfigurationSelector();
        appStore.summaryThumbnail = ctx.currentConfiguration.data.thumbnail;
    });

    const unregister = fetchIntercept.register({
        request: function (url, config) {
            // Modify the url or config here
            return [url, config];
        },

        requestError: function (error) {
            // Called when an error occured during another 'request' interceptor call
            return Promise.reject(error);
        },

        response: function (response) {
            // Modify the reponse object
            if (response.status === 401) {               
                appStore.loading = false;
                showPage("login")                            
            }           
            return response;
        },

        responseError: function (error) {
            // Handle an fetch error
            return Promise.reject(error);
        }
    });

    //Selectors
    // A material was picked in the material selector: apply it to all upholstery and save
    let handleMaterialSelected = async function () {

        pauseRender(ctx);
        appStore.loading = true;

        var entities = ctx.currentModule.entities.filter(x => productLibrary.getProductCategory(x.category) === "UPHOLSTRY");
        entities.forEach(entity => {
           
            let productVariant = getProductVariant(ctx, entity.productNo, entity);
            entity.variantProductNo = productVariant.productNo
            let entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);

            let material = materialLibrary.getMaterialByProductNumber(ctx.currentConfiguration.data["material"]);
            if (material) {
                materialLibrary.assignMaterial(productVariant, [entityNode], material, ctx.scene, function () {                    
                    entityNode.setEnabled(true);
                });
            }
        });

        resumeRender(ctx).then(() => {    
            ctx.ui.handleModuleUpdating();            
        });       

    }
    //Reset configurations
    let resetConfigurations = function () {
        localStorage.removeItem("Configurations");
    }

    let displayBackButton = function (show: boolean) {
        appStore.header.backButton = show;
    }

    let displaySummaryButton = function (show: boolean) {
        appStore.header.summaryButton = show;

        disableSummaryButton();
    }

    let displayUser = function(show: boolean) {
        appStore.header.userMenu = show;
    }

    let disableSummaryButton = function () {
        if (!ctx.currentModule || ctx.currentModule.entities.length === 0)
            appStore.header.summaryButtonDisabled = true;
    }

    let updateSeriesName = function (series: string) {
        appStore.header.seriesName = series;
    }

    let updateSummaryMenuTotalPrice = async function () {
        if (ctx.isB2C) return;
        if (ctx.currentConfiguration == undefined) {
            return;
        }       

        var moduleTotalPrice = await ctx.moduleHelper.getTotalModulePrice(ctx.currentModule, ctx.currentConfiguration);
        appStore.header.priceValue = FormatHelper.formatNumber(moduleTotalPrice.totalPrice);
        appStore.header.priceCurrency = moduleTotalPrice.currency;

    }

    let showPage = function (pageName: string) {

        ctx.previousPage = ctx.currentPage;

        // handlePageSetup sets appStore.page, which shows the page component
        handlePageSetup(pageName);
    }

    let handlePageSetup = async function (pageName: string) {

        ctx.currentPage = pageName;
        appStore.page = pageName;
        appStore.pageVisits++;

        displayBackButton(false);
        displaySummaryButton(false);
        displayUser(false)

        switch (pageName) {           
            case "configurations": {
                // ConfigurationsPage reloads the project list itself
                displayUser(true);
            }
                break;
            case "series": {

                //  clearConfigurationAssets();
                cleanupAssets(ctx);
                ctx.currentModule = null;
                ctx.currentModuleNode = null;

                displaySummaryMenu(false);
                displayUser(true);
                // SeriesPage resets to step 1 and reloads the series list itself

            }
                break;

            case "viewport": {
                updateConfigurationSelector();
                displaySummaryMenu(true);
                displaySummaryButton(true);
                displayUser(true);
                
            }
                break;
            case "summary": {
                displayBackButton(true);
                displayUser(true)
            }
            break;
            default: {
                displaySummaryButton(false);
                displayUser(false)
            }
            break;
        }
    }

    //Initialization
    let initApp = async function () {

        ctx.viewMode = "module";
        resetConfigurations();

        appStore.loading = true;
        setDisplayName();

        showPage("configurations");
      

        seriesLibrary.fetchSeries({ search: "" });
    }

    //Login
    let handleLogin = function () {

        if (ctx.user) {            
            initApp();  
        } else if (ctx.resetPassword) {  
            showPage("resetpassword")
        } else {
            showPage("login");
        }
    }

    let setDisplayName = function () {
        appStore.header.userName = ctx.user.displayName;
    }

    let initRenderer = async function () {

        return new Promise((resolve) => {
            ctx.canvas = document.getElementById("renderCanvas") as unknown as HTMLCanvasElement;
            ctx.engine = createEngine(ctx.canvas, ctx.scaleFactor);
            ctx.scene = createScene(ctx.engine);

            ctx.moduleHelper.scene = ctx.scene;  

            ctx.mainCamera = createMainCamera(ctx.scene);
            ctx.scene.activeCamera = ctx.mainCamera;
            ctx.mainCamera.attachControl(true, false);

            ctx.renderTargetCamera = createRenderTargetCamera("renderTarget",ctx.scene);
            ctx.topviewCamera = createRenderTargetCamera("topview", ctx.scene);

            createEnvironment(ctx.scene);

            ctx.light = createLight("DirectionalLight", ctx.scene, new BABYLON.Vector3(0, 0, 0), new BABYLON.Vector3(-0.38, -1, -0.38), 3, -20, 60);  
            ctx.sg = createShadowgenerator(ctx.light, 0, 0.15);   
            ctx.ground = createGround(ctx.light, ctx.scene);
        
            ctx.GUI = BABYLON.GUI.AdvancedDynamicTexture.CreateFullscreenUI("UI", true, ctx.scene, BABYLON.DynamicTexture.BILINEAR_SAMPLINGMODE);
            //GUI.renderAtIdealSize = true;

            ctx.rootNode = new BABYLON.TransformNode("root", ctx.scene);
            ctx.rootNode.position = BABYLON.Vector3.Zero();

            ctx.instanceNode = new BABYLON.TransformNode("instanceables", ctx.scene);

            ctx.modulesNode = new BABYLON.TransformNode("modules", ctx.scene);
            ctx.modulesNode.parent = ctx.rootNode;
            ctx.modulesNode.position = BABYLON.Vector3.Zero();

            ctx.productsNode = new BABYLON.TransformNode("products", ctx.scene);
            ctx.productsNode.parent = ctx.rootNode;

            meshLibrary.loadedMeshesNode = ctx.productsNode;
            meshLibrary.rootNode = ctx.modulesNode;
            meshLibrary.instanceNode = ctx.instanceNode;         

            ctx.scene.executeWhenReady(() => {                    

                /**
                 * Scene pointer move (dragging etc.)
                 * @param e
                 */
                ctx.scene.onPointerMove = function (e) {
                    if (ctx.currentEntityNode) {
                        if (ctx.isDragging) {

                            dragCanvas(ctx);

                            ctx.screenPointerPosition = new BABYLON.Vector2(ctx.scene.pointerX, ctx.scene.pointerY);
                            ctx.scenePointerPosition = getTargetPosition(ctx, ctx.screenPointerPosition.x, ctx.screenPointerPosition.y);
                            ctx.dragPosition = getZeroPlaneVector(ctx, ctx.mainCamera.position, ctx.scenePointerPosition);

                            //Move according to real center of entity
                            var centerNode = ctx.currentEntityNode.getChildTransformNodes(false, function (m) { return m.id === "center" })[0];
                            ctx.dragPosition.subtractInPlace(centerNode.position);

                            ctx.currentEntityNode.setAbsolutePosition(new BABYLON.Vector3(ctx.dragPosition.x, ctx.currentEntityNode.position.y, ctx.dragPosition.z));
                        }
                    }
                };

                /**
                 * Scene pointer up (stop drag etc.)
                 * @param e
                 */
                ctx.scene.onPointerUp = function (e) {

                    if (ctx.isPlacing) {

                        ctx.ui.handleModuleUpdating();

                        ctx.currentEntityNode = null;
                        ctx.isPlacing = false;                                    
                    }

                    if (ctx.isDragging) {
                        dragEndCanvas(ctx);                   
                    }
                };

                    /**
                     * Scene pointer down (click on scene, unselect meshes etc.)
                     * @param e
                     * @param pickInfo
                     * @param type
                     */
                    ctx.scene.onPointerDown = function (e, pickInfo, type) {
                        if (pickInfo.pickedMesh === null) {

                            if (ctx.isPlacing) {

                                ctx.isPlacing = false;

                                if (ctx.currentEntityNode) {

                                    hideAvailableConnectors(ctx);

                                    ctx.screenPointerPosition = new BABYLON.Vector2(ctx.scene.pointerX, ctx.scene.pointerY);
                                    ctx.scenePointerPosition = getTargetPosition(ctx, ctx.screenPointerPosition.x, ctx.screenPointerPosition.y);
                                    ctx.dragPosition = getZeroPlaneVector(ctx, ctx.mainCamera.position, ctx.scenePointerPosition);

                                    ctx.currentEntityNode.setAbsolutePosition(new BABYLON.Vector3(ctx.dragPosition.x, ctx.currentEntityNode.position.y, ctx.dragPosition.z));

                                    let currentEntity = getCurrentEntity(ctx);

                                    let productVariant = getProductVariant(ctx, currentEntity.productNo, currentEntity);

                                    var entityBounds = ctx.currentEntityNode.getChildMeshes(true, function (m) { return m.id === "entitybounds" })[0];
                                    entityBounds.setEnabled(true);

                                    //Load entity mesh
                                    meshLibrary.loadMeshAsync(productVariant.productNo, currentEntity, productVariant.type, "furniture/", productVariant.model + ".glb", productVariant.isFlipped, ctx.scene, false, ctx.sg, ctx.sg2, (e) => onProgress(ctx, e), function (clonedMeshEntityId, clonedMesh) {

                                        let loadedEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, clonedMeshEntityId);
                                        let loadedProductVariant = getProductVariant(ctx, loadedEntity.productNo, loadedEntity);

                                        clonedMesh.scaling.x = loadedProductVariant.isFlipped ? -1 : 1;
                                        clonedMesh.position.y = ctx.currentConfiguration.data.offsetY;

                                        //Set material
                                        let material = materialLibrary.getMaterialByProductNumber(ctx.currentConfiguration.data["material"]);
                                        if (material) {
                                            materialLibrary.assignMaterial(loadedProductVariant, [clonedMesh], material, ctx.scene, function () {                                           
                                                clonedMesh.setEnabled(true);                                               
                                            });
                                        }
                                     
                                        //Create leg attachments                                      
                                        createLegAttachments(ctx, ctx.currentModule, currentEntity, loadedProductVariant, ctx.scene).then(() => {
                                            
                                            //Create legs
                                            constructLegs(ctx, ctx.currentModule, currentEntity);
                                        });
                                                                  
                                    });  
                                 
                                }

                            } else {
                                hideHighlight(ctx, ctx.currentModuleNode);
                                hideEntityActions(ctx);
                                ctx.currentEntityNode = null;                                
                            }
                        } else {

                            if (ctx.currentEntityNode) {
                                let entityNode = getParentMesh(ctx, pickInfo.pickedMesh, "entity") as BABYLON.Mesh;
                                if (entityNode.id === ctx.currentEntityNode.id) {

                                    ctx.mainCamera.detachControl();
                                    dragStartCanvas(ctx);

                                    showHighlight(ctx, ctx.currentEntityNode);

                                    ctx.screenPointerPosition = new BABYLON.Vector2(ctx.scene.pointerX, ctx.scene.pointerY);

                                    ctx.scenePointerPosition = getTargetPosition(ctx, ctx.screenPointerPosition.x, ctx.screenPointerPosition.y);
                                    ctx.dragPosition = getZeroPlaneVector(ctx, ctx.mainCamera.position, ctx.scenePointerPosition);

                                    //Move according to real center of entity
                                    var centerNode = ctx.currentEntityNode.getChildTransformNodes(false, function (m) { return m.id === "center" })[0];
                                    ctx.dragPosition.subtractInPlace(centerNode.position);

                                    ctx.currentEntityNode.setAbsolutePosition(new BABYLON.Vector3(ctx.dragPosition.x, ctx.currentEntityNode.position.y, ctx.dragPosition.z));
                                }
                            }

                        }
                    }

                    /**
                     * Scene pointer pick (picking meshes etc.)
                     * @param e
                     * @param pickInfo
                     */
                    ctx.scene.onPointerPick = function (e, pickInfo) {

                        hideEntityActions(ctx);
                        // currentEntityNode = null;

                        if (e.button === 0) {
                            if (pickInfo.pickedMesh !== null) {

                                let mesh = pickInfo.pickedMesh;
                                let moduleNode = getParentMesh(ctx, mesh, "module") as BABYLON.TransformNode;
                                let module = ctx.currentConfiguration.data.modules.find(function (m) { return m.id === moduleNode.id });

                                ctx.currentModule = module;
                                ctx.currentModuleNode = moduleNode;
                        
                                if (ctx.isPlacing) {
                                    ctx.isPlacing = false;
                                    hideAvailableConnectors(ctx);
                                } else {

                                    let entityNode = getParentMesh(ctx, mesh, "entity") as BABYLON.Mesh;
                                    hideHighlight(ctx, moduleNode);

                                    if (entityNode) {
                                        ctx.currentEntityNode = entityNode;
                                        showHighlight(ctx, entityNode);
                                        showEntityActions(ctx);
                                    }
                                }                      
                            }
                        }
                    }

                    /**
                     * Renderlopp
                     */
                    ctx.engine.runRenderLoop(function () {
                        if (ctx.render) {
                            ctx.scene.render();
                        }
                    });
            
                    ctx.rendererInitialized = true;
                    resolve(true);            

            })
        });
    }

    // Babylon inspector (Alt key) on development hosts
    const isDevelopmentHost = window.location.hostname.indexOf("localhost") >= 0 || window.location.hostname.indexOf("cadesignform") >= 0;
    if (isDevelopmentHost) {
        enableDebug(ctx);
    }


    // Shows a still of the current 3D view (drawn into #loadCanvas) while the module is rebuilt
    let showLoadingCanvas = function () {

        appStore.loading = true;
        appStore.loadingCanvas = true;

        const loadCanvas = document.getElementById("loadCanvas") as unknown as HTMLCanvasElement;
        loadCanvas.width = ctx.engine.getRenderWidth();
        loadCanvas.height = ctx.engine.getRenderHeight();

        ctx.scene.render();
        loadCanvas.getContext("2d").drawImage(ctx.engine.getRenderingCanvas(), 0, 0, ctx.engine.getRenderWidth(), ctx.engine.getRenderHeight());
    }

    let hideLoadingCanvas = function () {
        appStore.loadingCanvas = false;
        appStore.loading = false;
    }

    /* let toggleMetrics = function (show) {
 
         Metrics.ClearMetrics(scene, rulerGUI);
         if (show) {
             Metrics.CreateModuleRulers(currentModule, productLibrary, scene, rulerGUI);
         }
     }*/

    let createProjectConfiguration = async function (project: Project, series: string) {

        let configuration = {
            key: undefined,
            series: series,
            data: {
                id: "configuration" + Date.now(),
                offsetY: 0,
                material: "",
                leg: "",
                thumbnail: "",
                modules: [],
                options: {},
                customOptions: [{id: "Removable cover", value: "Removable cover"}]
            }
        } as Configuration;

        projectLibrary.createConfiguration(project, configuration);
        ctx.currentConfiguration = configuration;

    }

    // A series was chosen in the series wizard: create the configuration for it
    let selectSeries = async function (seriesId: number) {

        const series = await seriesLibrary.fetchSeriesByID(seriesId);

        //Create configuration
        createProjectConfiguration(ctx.currentProject, series.name);

        //Set default legs for series
        var defaultLeg = seriesLibrary.getSeriesLegPackage(series, "standard");
        ctx.currentConfiguration.data.leg = ctx.currentConfiguration.data.leg || defaultLeg.identifier;

        materialLibrary.defaultMaterial = series.defaultMaterial;
        materialLibrary.materials = series.materials;

        return series;
    }

    let saveProjectConfiguration = async function () {

        if (ctx.currentConfiguration) {
            ctx.currentConfiguration.data.modules.forEach(function (module) {

                var moduleNode = ctx.scene.getTransformNodeByUniqueId(module.uniqueId);
                if (moduleNode) {
                    module.position = moduleNode.position;
                    module.rotation = moduleNode.rotation;
                }

                module.entities.forEach(function (entity) {

                    var entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);
                    if (entityNode) {
                        entity.position = entityNode.position;
                        entity.rotation = entityNode.rotation;
                    }
                });

            });

            ctx.currentProject.configurations = ctx.currentProject.configurations.filter(x => x.data.id !== ctx.currentConfiguration.data.id);           
            ctx.currentProject.configurations.push(ctx.currentConfiguration);
        }

        return await projectLibrary.saveProject(ctx.currentProject);
    }




    

    let validateConstruction = function (module: Module) {

        resetErrors();
        
        var filteredEntities = module.entities.filter(function (entity: Entity) { return productLibrary.getProductCategory(entity.category) === "UPHOLSTRY" });
        for (let entity of filteredEntities) {              
            
            var product = productLibrary.getProductByProductNo(entity.productNo);

            const adjacentEntities = ctx.entityHelper.getAdjacentEntities(module, entity, ctx.scene);
            let rotationMatrix = BABYLON.Matrix.RotationYawPitchRoll(-entity.rotation._y, entity.rotation._x, entity.rotation._z);        

            let rightOffsetZ = 0;
            let rightDepth = 0;
            let entityRight = adjacentEntities.find(x => ["RIGHT"].includes(x.direction));         
            let entityRightConnectionType = ""  
            let entityProductRight = null;
            if (entityRight) {
                entityProductRight = productLibrary.getProductByProductNo(entityRight.entity.productNo);               
                const rightBb = ctx.entityHelper.getEntityBoundingBox(entityRight.entity, ctx.scene);
                let entityExtendSize = BABYLON.Vector3.TransformCoordinates(rightBb.extendSize, rotationMatrix);  
                entityRightConnectionType = ctx.moduleHelper.getEntityConnectionType(ctx.currentModule, entityRight.entity)   
                rightDepth = entityProductRight.depth;
                rightOffsetZ = entityExtendSize.z * 2;
            }  
            
            let leftOffsetZ = 0;
            let leftDepth = 0;
            let entityLeft = adjacentEntities.find(x => ["LEFT"].includes(x.direction));
            let entityLeftConnectionType = ""  
            let entityProductLeft = null             
            if (entityLeft) {
                entityProductLeft = productLibrary.getProductByProductNo(entityLeft.entity.productNo);
                const leftBb = ctx.entityHelper.getEntityBoundingBox(entityLeft.entity, ctx.scene);
                let entityExtendSize = BABYLON.Vector3.TransformCoordinates(leftBb.extendSize, rotationMatrix);  
                entityLeftConnectionType = ctx.moduleHelper.getEntityConnectionType(ctx.currentModule, entityLeft.entity)   
                leftDepth = entityProductLeft.depth;
                leftOffsetZ = entityExtendSize.z * 2;
            } 

            var entityConnections = entity.connections.map(function (c) { return c.id });

            let entityMesh = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);

            if (!entityMesh)
                continue;

            var entityBounds = entityMesh.getChildMeshes(true, function (cm) { return cm.id === "entitybounds" })[0] as BABYLON.AbstractMesh;
            let bv1 = entityMesh.getHierarchyBoundingVectors();
            let bb1 = new BABYLON.BoundingBox(bv1.min, bv1.max);  

            if (entity.connections.length === 0 && !productLibrary.hasProductVariants(product, ["CT00"])) {
                addError(i18next.t("error.nofreestanding", { type: product.name }));
            }

            filteredEntities.forEach(function (entityCheck: Entity) {
                if (entity.id !== entityCheck.id) {
                    if (entity.connections.length === 0) {

                         let entityCheckMesh = ctx.scene.getTransformNodeByUniqueId(entityCheck.uniqueId);
                         let entityCheckBounds = entityCheckMesh.getChildMeshes(true, function (cm) { return cm.id === "entitybounds" })[0] as BABYLON.AbstractMesh;
                         let bv2 = entityCheckMesh.getHierarchyBoundingVectors();
                         let bb2 = new BABYLON.BoundingBox(bv2.min, bv2.max);
                      
                         if (entityBounds.intersectsMesh(entityCheckBounds,false))
                             addError(i18next.t("error.intersect"));
                         }
                    }                 
            });

            if (product.type === "CENTER_SECTION") {

                if (!productLibrary.hasProductVariants(product, ["CT00"])) {

                    const variants = productLibrary.getProductVariants(product);

                    if (variants.length === 1) {
                         if (variants.includes("CT02")) {
                            if (!["CT03","CT04"].every(x => entityConnections.includes(x))) {
                                addError(i18next.t("error.connectionmissing", { type: product.name })); 
                            }
                         }
                    }                 
                               
                }
            }            

            if (product.type === "ARM") {

                //Bure
                if ([148882,157547,157546].includes(product.series)) {

                    const leftLength = adjacentEntities.filter(x => x.direction === "LEFT").length;
                    const rightLength = adjacentEntities.filter(x => x.direction === "RIGHT").length;

                    if (leftLength > 0 && leftLength < 3 ||
                        rightLength > 0 &&  rightLength < 3) {
                            if (entityConnections.includes("CT44_center") || 
                                entityConnections.includes("CT45_center")) {
                                    addError(i18next.t("error.connectionmissing", { type: product.name }));
                                }                       
                    }
                }

            }

            if (product.type === "CORNER_SECTION") {
                if (entity.connections.length < 2) {
                    addError(i18next.t("error.cornersection", { type: product.name }));
                }
            }


            if (product.type === "BACK") {
                if (!(entityConnections.includes("CT37") || entityConnections.includes("CT11"))) {
                    addError(i18next.t("error.frontconnect", { type: product.name }));
                }
            }

            if (product.type === "SEATFRAME") {

                //Bure
                if ([148882,157547,157546].includes(product.series)) {
                    
                    if (product.depth < 83 && (entityRight?.entity.category === "BACK" || entityLeft?.entity.category === "BACK")) {                        
                         addError(i18next.t("error.illegalconnection", { type: product.name }));
                    }                 

                    if (product.depth < 83 && (entityRight?.entity.category === "ARM" || entityLeft?.entity.category === "ARM")) {
                        if (entityLeftConnectionType === "CT45" || entityRightConnectionType === "CT44") {
                            if ((entityProductLeft?.depth ?? 0) >= 120 || (entityProductRight?.depth ?? 0) >= 120) {
                                addError(i18next.t("error.illegalconnection", { type: product.name }));
                            } 
                        }
                    }

                    if (adjacentEntities.filter(x => x.direction === "BACK").length >= 1 && !entity.connections.map(x => x.id).includes("CT11")) {
                         addError(i18next.t("error.intersect"));
                    }

                }               
             
            }
        };

        appStore.header.summaryButtonDisabled = appStore.errors.length > 0;

    }

    let openConfig = async function (identifier: string) {

        if (identifier) {            

            let project = await projectLibrary.getProject(identifier);
            ctx.currentProject = project;
            ctx.currentConfiguration = project.configurations[0];

            if (!ctx.currentConfiguration)
                return false;

            if (ctx.currentConfiguration.data.modules.length > 0)
                ctx.currentModule = ctx.currentConfiguration.data.modules[0];

            var series = await seriesLibrary.fetchSeriesByName(ctx.currentConfiguration.series);
            var defaultLeg = seriesLibrary.getSeriesLegPackage(series, "standard");

            ctx.currentConfiguration.data.leg = ctx.currentConfiguration.data.leg || defaultLeg.type;

            materialLibrary.defaultMaterial = series.defaultMaterial;
            materialLibrary.materials = series.materials;

            renderProject().then(() => {
                constructModule(ctx, ctx.currentModule).then(() => {
                    resetMainCamera(ctx);
                    adjustCameraView(ctx, ctx.mainCamera, ctx.currentModuleNode, ctx.scene, Math.PI/2.5, Math.PI/2.5, ctx.mainCamera.radius, ctx.defaultMinCameraRadius);   
                   // handleModuleUpdating();
                });
            });          
        }
    }

    // Arms can be placed at the back, center or front of a connector: ask the user when there is a choice
    let createEntityPlacementPopup = function(entity: Entity, matchingConnectors: string[]): Promise<string | undefined> {

        if (entity.category !== "ARM") {
            return Promise.resolve(undefined);
        }

        const placements = matchingConnectors
            .filter(connector => connector.includes("_back") || connector.includes("_center") || connector.includes("_front"))
            .map(connector => connector.split("_")[1]);

        if (placements.length === 0) {
            return Promise.resolve(undefined);
        }

        return askPlacement(placements);
    }

    let handleModuleUpdating = function(callback?: () => void) {
        ctx.scene.executeWhenReady(() => {
            renderConfigurationThumbnail(ctx, ctx.currentConfiguration).then(async (result) => {
            
                ctx.currentConfiguration.data.thumbnail = result;
                window.dispatchEvent(new Event("thumbnail_rendered"));

                await updateSummaryMenuTotalPrice();

                validateConstruction(ctx.currentModule);              
                ctx.mainCamera.attachControl(true, false);

                saveProjectConfiguration().then(() => {                    
                    ctx.ui.hideLoadingCanvas();
                    if (typeof(callback) === "function")
                        callback();
                });
            });     
       });  
    }

    //Drag & Drop  

    const renderCanvasDrop = document.getElementById("renderCanvasDrop")
    dragDropHandler.createDroppable("renderDropTarget", renderCanvasDrop)
    
    dragDropHandler.manager.monitor.addEventListener("dragstart", (e) => {            
        dragUIStart(ctx, e);
    });

    dragDropHandler.manager.monitor.addEventListener("dragmove", (e, m) => {  
       
        if (e.operation.target && e.operation.target.id === "renderDropTarget") { 

            var dragger = document.querySelector("[data-dnd-dragging]")
            dragger.classList.add("dragging")
      
            dragUI(ctx, e);
        }        
    });

    dragDropHandler.manager.monitor.addEventListener("dragend", (e) => {             

        if (e.operation.target && e.operation.target.id === "renderDropTarget") {     

            var dragger = document.querySelector("[data-dnd-dragging]")
            dragger.classList.remove("dragging")         
            dragUIEnd();            
        }        
    });

    

    //Drag End (UI)
    var dragUIEnd = async function () {

        if (!ctx.currentEntityNode && !ctx.currentModuleNode) {
            ctx.isDragging = false;
            //return false;
        }

        if (ctx.isDragging) {

            ctx.isDragging = false;

            handleDragEnd(ctx);
            hideAvailableConnectors(ctx);
            hideGhostEntity(ctx);

           // await updateSummaryMenuTotalPrice();
        }
    };

    var debounce = function (fn, delay) {
        var timer = null;
        return function () {
            var context = this, args = arguments;
            clearTimeout(timer);
            timer = setTimeout(function () {
                fn.apply(context, args);
            }, delay);
        };
    }

    // Shows the project's configurations as tiles next to the 3D view (ConfigurationSelector in ViewportPage)
    let updateConfigurationSelector = function () {
        ctx.currentProject.configurations.sort((a, b) => { return a.data.id > b.data.id ? 1 : -1 });
        appStore.configurations.items = ctx.currentProject.configurations.map(configuration => ({ id: configuration.data.id, thumbnail: configuration.data.thumbnail }));
        appStore.configurations.activeId = ctx.currentConfiguration.data.id;
        appStore.configurations.canAdd = ctx.currentProject.configurations.length < 4;
    }

    let renderProject = async function () {

        return new Promise(async (resolve) => {

            let series = await seriesLibrary.fetchSeriesByName(ctx.currentConfiguration.series);
            updateSeriesName(series.name);
                   
            await productLibrary.fetchProducts(series.id)

            var selectedMaterial = materialLibrary.getMaterialByProductNumber(ctx.currentConfiguration.data["material"]);
            if (selectedMaterial) {
                resetProductSelector();

                showMaterialSelection(ctx.currentConfiguration, selectedMaterial.group);
            }

            refreshProductSelector();
            updateConfigurationSelector();


            if (!ctx.rendererInitialized) {
                initRenderer().then(() => {
                    resolve(true);
                });
            } else {
                resolve(true);
            }

            showPage("viewport");
        
        });

    }

    // Calculates the summary page (SummaryPage.vue). Only updated when there are no construction errors
    // and the module has elements or accessories - otherwise the page keeps its previous content.
    let updateSummary = async function () {
        if (appStore.errors.length === 0) {

            const series = await seriesLibrary.fetchSeriesByName(ctx.currentConfiguration.series)
            const legThumb = seriesLibrary.getLegThumbnail(series, ctx.currentConfiguration.data.leg);
            const leg = series.legPackages.find(e => e.identifier === ctx.currentConfiguration.data.leg);

            const summaryList = await ctx.moduleHelper.generateModuleSummaryList(ctx.currentModule, ctx.currentConfiguration);
            var totalPrice = await ctx.moduleHelper.getTotalModulePrice(ctx.currentModule, ctx.currentConfiguration);
            const options = [];

            series.customOptions.forEach(option => {
                var selection = ctx.currentConfiguration.data.customOptions?.find(e => e.id == option.id);

                if (selection != undefined && selection.value == option.id) {
                    options.push(option.name);
                }
            })

            if (summaryList.elements.length > 0 || summaryList.accessories.length > 0) {

                let currentMaterial = materialLibrary.getMaterialByProductNumber(ctx.currentConfiguration.data.material) as Material;
                let currency = summaryList.elements[0].currency;

                // Elements, each followed by its info text and extras; then the configuration's custom options
                const rows: SummaryRow[] = [];
                summaryList.elements.forEach(summaryItem => {
                    var product = productLibrary.getProductByProductNo(summaryItem.productNo);

                    rows.push({
                        className: summaryItem.customerInfoText !== "" ? "no-border" : undefined,
                        cells: [
                            { html: product.name },
                            { html: ProductHelper.getFormattedString(product, ['width', 'depth', 'height']) },
                            { html: `${summaryItem.axProductNo} - ${summaryItem.variantProductNo}` },
                            { html: `${summaryItem.amount} stk` },
                            { html: `${FormatHelper.formatNumber(summaryItem.price)} ${currency}` },
                        ],
                    });

                    if (summaryItem.customerInfoText !== "") {
                        rows.push({ className: "info-text", cells: [{ html: summaryItem.customerInfoText, colspan: 5 }] });
                    }

                    summaryItem.extras.forEach(extra => {
                        rows.push({
                            cells: [
                                { html: "" },
                                { html: extra.name, className: "pl-6" },
                                { html: extra.productNo },
                                { html: "" },
                                { html: `${FormatHelper.formatNumber(extra.price)} ${currency}` },
                            ],
                        });
                    });
                });

                summaryList.customOptions.forEach(customOption => {
                    rows.push({
                        cells: [
                            { html: customOption.productNo },
                            { html: "" },
                            { html: customOption.name },
                            { html: `${customOption.amount} stk` },
                            { html: `${FormatHelper.formatNumber(customOption.totalprice)} ${currency}` },
                        ],
                    });
                });

                // All accessories of the series with the amount in this configuration
                const accessories: SummaryAccessory[] = series.accessories.map(addOn => {
                    var amount = summaryList.accessories.find(e => e.productNo == addOn.productNumber)?.amount ?? 0;
                    var price = addOn.priceLists[0]?.prices[currentMaterial.priceGroup] ?? 0;

                    series.customOptions.forEach(option => {
                        const selection = ctx.currentConfiguration.data.customOptions?.find(e => e.id == option.id);
                        if (selection) {
                            var pricelist = option.priceLists.find(e => e.productType == "Accessories" && e.accessories.includes(addOn.name));

                            if (pricelist) {
                                price += pricelist.price.price ?? 0
                            }
                        }
                    })

                    return {
                        name: addOn.name,
                        productNumber: addOn.productNumber,
                        amount,
                        price: `${price} ${currency}`,
                        total: `${FormatHelper.formatNumber(price * amount)} ${currency}`,
                    };
                });

                appStore.summaryThumbnail = ctx.currentConfiguration.data.thumbnail;
                appStore.summary = {
                    image: ctx.currentConfiguration.image,
                    seriesName: ctx.currentConfiguration.series,
                    materialImage: materialLibrary.getMaterialThumbnailUrl(currentMaterial.productNumber),
                    materialName: currentMaterial.name,
                    legsImage: legThumb,
                    legsName: leg?.name || leg?.identifier || "",
                    optionsHtml: options.join("<br />"),
                    drawing: ctx.currentConfiguration.drawing,
                    configNumber: ctx.currentProject.identifier.toUpperCase(),
                    rows,
                    accessories,
                    total: `${FormatHelper.formatNumber(totalPrice.totalPrice)} ${totalPrice.currency}`,
                };
            }
        }
    }

    let displaySummaryMenu = function (isShown: boolean) {
        appStore.header.summaryMenu = isShown;
    }


    let startConfiguring = async function () {

        appStore.loading = true;

        if (ctx.isNewConfig) {
            await projectLibrary.createProject(ctx.currentProject).then((newProject) => {
                ctx.currentProject = newProject;                    
            });
        } else {
            resetErrors();
            await saveProjectConfiguration();
        }

        renderProject().then(() => {
            ctx.mainCamera.radius = ctx.initialCameraRadius;
            appStore.loading = false;
        });

    }

    let handleSummaryClicked = function () {   
        
        hideAllHighlights(ctx);
        hideEntityActions(ctx);

        renderDrawingWithGUI(ctx, ctx.currentConfiguration, () => {
            renderConfigurationImage(ctx, ctx.currentConfiguration, undefined, (image) => {
                ctx.currentConfiguration.image = image;    
                updateSummary();
            }, 1, Math.PI / 2, Math.PI / 2.5, 1.7, 4);
        });

        showPage("summary");
    }

    // Summary page: accessory amount changed
    let handleAccessoryAmountChanged = async function (productNo: string, value: number) {
        const amount = Math.max(0, value);

        const summary = await ctx.moduleHelper.generateModuleSummaryList(ctx.currentModule, ctx.currentConfiguration);
        const summaryItem = summary.accessories.find(e => e.productNo == productNo);

        const dif = amount - (summaryItem?.amount ?? 0);

        if (!ctx.currentConfiguration.data.addons) {
            ctx.currentConfiguration.data.addons = []
        }

        let addOnItem = ctx.currentConfiguration.data.addons.find(e => e.productNo == productNo);
        if (!addOnItem) {
            addOnItem = {
                amount: 0,
                productNo: productNo
            }
            ctx.currentConfiguration.data.addons.push(addOnItem);
        }
        addOnItem.amount += dif;

        updateSummary();
    }

    // Summary page "Order" (only possible once terms and disclaimer are accepted)
    let placeOrder = async function () {
        await saveProjectConfiguration();
        pdfHandler.generatePDF(ctx.currentProject, ctx.currentConfiguration, ctx.moduleHelper, ctx.currentModule);
    }


    let handleBackClicked = function () {

        switch (ctx.currentPage) {

            case "configurations": {
                showPage(ctx.previousPage);
            }
                break;
            case "summary": {

                resetGUI(ctx);              
                showPage("viewport");
            }
                break;
        }

    }


    // Configuration tile clicked (the tile is already marked active by the component)
    let selectConfiguration = async function (configurationId: string) {

        if (ctx.currentConfiguration.data.id !== configurationId) {          

            cleanupAssets(ctx);
            resetErrors();
            ctx.ui.showLoadingCanvas();

            ctx.currentConfiguration = projectLibrary.getConfiguration(ctx.currentProject, configurationId);         

            var series = await seriesLibrary.fetchSeriesByName(ctx.currentConfiguration.series);            
            updateSeriesName(series.name);  

            materialLibrary.defaultMaterial = series.defaultMaterial;
            materialLibrary.materials = series.materials;

            productLibrary.fetchProducts(series.id).then(() => {
                let currentMaterial = materialLibrary.getMaterialByProductNumber(ctx.currentConfiguration.data["material"]);

                resetProductSelector();

                showMaterialSelection(ctx.currentConfiguration, currentMaterial.group);

                ctx.currentModule = ctx.currentConfiguration.data.modules[0];
            
                constructModule(ctx, ctx.currentModule).then(() => {
                    resetMainCamera(ctx);                                           
                    adjustCameraView(ctx, ctx.mainCamera, ctx.currentModuleNode, ctx.scene, Math.PI/2.5, Math.PI/2.5, ctx.mainCamera.radius, ctx.defaultMinCameraRadius);                  
                });
            });
           
        }

    }

    // Trash icon on a configuration tile
    let deleteConfiguration = async function (configurationId: string) {

        var deleteConfiguration = ctx.currentProject.configurations.find(x => x.data.id === configurationId);
        projectLibrary.deleteConfiguation(ctx.currentProject, deleteConfiguration);       
       
        if (ctx.isSeriesConstrained) {    
            
            cleanupAssets(ctx);          

            if (ctx.currentProject.configurations.length > 0) {
                ctx.currentConfiguration = ctx.currentProject.configurations[0];
                ctx.currentModule = ctx.currentConfiguration.data.modules[0];

                constructModule(ctx, ctx.currentModule).then(() => {                       
                    adjustCameraView(ctx, ctx.mainCamera, ctx.currentModuleNode, ctx.scene, ctx.mainCamera.alpha, ctx.mainCamera.beta, ctx.mainCamera.radius, ctx.defaultMinCameraRadius);
                })  
            }
          
            updateConfigurationSelector();
            
        } else {

            if (ctx.currentProject.configurations.length > 0) {

                cleanupAssets(ctx);
                
                ctx.currentConfiguration = ctx.currentProject.configurations[0];

                let series = await seriesLibrary.fetchSeriesByName(ctx.currentConfiguration.series);                
                updateSeriesName(series.name);

                var currentMaterial = materialLibrary.getMaterialByProductNumber(ctx.currentConfiguration.data["material"]);

                productLibrary.fetchProducts(series.id).then(() => {
                    resetProductSelector();

                    showMaterialSelection(ctx.currentConfiguration, currentMaterial.group);

                    updateConfigurationSelector();

                    ctx.currentModule = ctx.currentConfiguration.data.modules[0];
                    constructModule(ctx, ctx.currentModule).then(() => {
                        adjustCameraView(ctx, ctx.mainCamera, ctx.currentModuleNode, ctx.scene, ctx.mainCamera.alpha, ctx.mainCamera.beta, ctx.mainCamera.radius, ctx.defaultMinCameraRadius);                                
                    })                
                });
                      
            } else {
                showPage("series");
            }
        }

        await projectLibrary.saveProject(ctx.currentProject);
         
    }

    // "+" tile: a new configuration in the same project
    let newConfiguration = async function () {

        if (!ctx.isSeriesConstrained) {
            showPage("series");
            resetProductSelector();
            resetMainCamera(ctx);
        } else {
            const series = await seriesLibrary.fetchSeriesByID(ctx.seriesConstrainedId);
            createProjectConfiguration(ctx.currentProject, series.name)           

            cleanupAssets(ctx);
            //resetMainCamera();
            updateConfigurationSelector();
            ctx.currentModule = ctx.currentConfiguration.data.modules[0];

            resetErrors();
            constructModule(ctx, ctx.currentModule).then(() => {
                adjustCameraView(ctx, ctx.mainCamera, ctx.currentModuleNode, ctx.scene, ctx.mainCamera.alpha, ctx.mainCamera.beta, ctx.mainCamera.radius, ctx.defaultMinCameraRadius);                
            })   
        }

    }


    let handleProjectsClicked = function () {

        showPage("configurations");

    }


    // A product in the product selector was clicked. The first module is placed directly; otherwise the
    // product waits at the available connectors. Stops the click so the viewport's click-outside doesn't cancel it.
    let handleProductClicked = function (productNumber: string, e: MouseEvent) {

        if (ctx.isPlacing)
            return;

        let product = productLibrary.getProductByProductNo(productNumber);

        ctx.currentProduct = product;
        instantiateModule(ctx, ctx.currentConfiguration, ctx.currentModule, function (module, moduleNode) {

            ctx.currentModule = module;
            ctx.currentModuleNode = moduleNode;

            instantiateEntity(ctx, ctx.currentModule, product, null, function (instantiatedEntity, instantiatedEntityNode) {

                hideAllHighlights(ctx);
                hideEntityActions(ctx);

                var productVariant = getProductVariant(ctx, instantiatedEntity.productNo, instantiatedEntity);
                instantiatedEntity.variantProductNo = productVariant.productNo;
                instantiatedEntityNode.metadata.productNo = productVariant.productNo;

                //Set current selected entity                
                ctx.currentEntityNode = instantiatedEntityNode;

                if (ctx.currentModule.entities.length == 1) {

                    ctx.isPlacing = false;

                    ctx.currentEntityNode.setAbsolutePosition(new BABYLON.Vector3(0, ctx.currentEntityNode.position.y, 0));

                    var entityBounds = ctx.currentEntityNode.getChildMeshes(true, function (m) { return m.id === "entitybounds" })[0];
                    entityBounds.setEnabled(false);

                    ctx.currentEntityNode = null;

                    meshLibrary.loadMeshAsync(productVariant.productNo, instantiatedEntity, productVariant.type, "furniture/", productVariant.model + ".glb", productVariant.isFlipped, ctx.scene, false, ctx.sg, ctx.sg2, (e) => onProgress(ctx, e), function (clonedMeshEntityId, clonedMesh) {
                                              
                        let loadedEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, clonedMeshEntityId);
                        let loadedProductVariant = getProductVariant(ctx, loadedEntity.productNo, loadedEntity);

                        clonedMesh.scaling.x = productVariant.isFlipped ? -1 : 1;
                        clonedMesh.position.y = ctx.currentConfiguration.data.offsetY;

                        //Set material
                        let material = materialLibrary.getMaterialByProductNumber(ctx.currentConfiguration.data["material"]);
                        if (material) {
                            materialLibrary.assignMaterial(loadedProductVariant, [clonedMesh], material, ctx.scene, function () {                           
                                clonedMesh.setEnabled(true);                             
                            });
                        }

                        hideAllHighlights(ctx);
                        hideEntityActions(ctx);
                        
                        //moveAllEntities(currentModule);
                        constructEntities(ctx, ctx.currentModule, () => {

                            ctx.legConnections = [];
                                                         
                            ctx.currentModule.entities.filter(x => productLibrary.getProductCategory(x.category) === "UPHOLSTRY").forEach(function (entity: Entity) {       
                                var productVariant = getProductVariant(ctx, entity.productNo, entity);

                                //Create leg attachments                          
                                createLegAttachments(ctx, module, entity, productVariant, ctx.scene).then(() => {
                                    
                                    //Create legs
                                    constructLegs(ctx, module, entity);
                                });
                            });                        

                            displaySummaryButton(true);                                 

                            pauseRender(ctx);
                            resumeRender(ctx).then(() => {
                                adjustCameraView(ctx, ctx.mainCamera, ctx.currentModuleNode, ctx.scene, Math.PI/2.5, Math.PI/2.5,  ctx.mainCamera.radius, ctx.defaultMinCameraRadius);                               
                            });
                        });

                    });
                } else {
                    ctx.isPlacing = true;
                    hideEntityActions(ctx);
                    updateAvailableConnectors(ctx, ctx.currentModule, instantiatedEntity)
                   // showAvailableConnectors(currentModule, instantiatedEntity)
                    e.preventDefault();
                    e.stopPropagation();
                }
            });
        });
    }

    // A click outside the 3D view cancels placing a module
    let handleClickOutsideViewport = function () {
        if (ctx.isPlacing) {
            ctx.isPlacing = false;
            const entity = getCurrentEntity(ctx);
            if (entity) {
                deleteEntity(ctx, entity);
                hideAvailableConnectors(ctx);
            }
        }
    }

}
